import { useCallback, useMemo } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import FlashContext from "components/FlashMessages/FlashContext";
import { ClockRewind, GitBranch, Terminal } from "components/icons/generated";
import ManagedStateInfo from "components/ManagedStateInfo";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoBlueprint from "components/MetaInfoList/Blueprint";
import MetaInfoListItem from "components/MetaInfoList/Item";
import MetaInfoProjectRoot from "components/MetaInfoList/ProjectRoot";
import MetaInfoStackCommit from "components/MetaInfoList/StackCommit";
import MetaInfoVendor from "components/MetaInfoList/Vendor";
import MetaInfoSpaceliftCommit from "components/MetaInfoSpaceliftCommit";
import MetaInfoSpaceliftRepo from "components/MetaInfoSpaceliftRepo";
import DesktopView from "components/Mobile/DesktopView";
import MobileView from "components/Mobile/MobileView";
import SpacePath from "components/SpacePath";
import StackStateBadge from "components/StackStateBadge";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import { projectRootTooltipContent } from "components/TooltipSnippets";
import VCSDetachedIndicator from "components/VCSDetachedIndicator";
import ViewHeader, { ViewHeaderProps } from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderNavigationItems from "components/ViewHeader/Navigation/Items";
import ViewHeaderNavigationResize from "components/ViewHeader/Navigation/Resize";
import { type NavigationItem } from "components/ViewHeader/Navigation/types";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { VCS_PROVIDERS_ICONS } from "constants/providerIcons";
import { VENDOR_CONFIG_TYPENAME } from "constants/vendor";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import FormToggleField from "ds/components/Form/ToggleField";
import useTypedContext from "hooks/useTypedContext";
import useCanDisableStack from "shared/Stack/access/useCanDisableStack";
import useCanEnableStack from "shared/Stack/access/useCanEnableStack";
import useSetStackFavorite from "shared/Stack/useSetStackFavorite";
import { VcsProvider } from "types/generated";
import { isAnsibleStackVendor } from "utils/stack";
import { generateBranchUrl, generateRepoUrl, generateRepoUrlText } from "utils/urls";
import { providerHasVcsHost } from "utils/vcs";
import { StackContext } from "views/Stack/Context";
import { showStackDetailsDrawer } from "views/Stack/DetailsDrawer";

import DisableButton from "../DisableButton";
import EnableButton from "../EnableButton";
import { showTerraformGeneratedCodeDrawer } from "../TerraformGeneratedCodeDrawer";
import StackHeaderAskButton from "./AskButton";
import styles from "./styles.module.css";

enum MenuItems {
  Runs = "Runs",
  IgnoredRuns = "IgnoredRuns",
  Tasks = "Tasks",
  PRs = "PRs",
  Environment = "Environment",
  Hooks = "Hooks",
  Contexts = "Contexts",
  IacManagement = "IacManagement",
  ConfigurationManagement = "ConfigurationManagement",
  Outputs = "Outputs",
  Dependencies = "Dependencies",
  StateHistory = "StateHistory",
  Notifications = "Notifications",
  Scheduling = "Scheduling",
  Policies = "Policies",
  Workspaces = "Workspaces",
  Settings = "Settings",
}

const hideOrder = [MenuItems.StateHistory, MenuItems.Hooks, MenuItems.IacManagement];

const REFETCH_QUERIES = ["GetStack"];

type StackHeaderProps = {
  noSticky?: ViewHeaderProps["noSticky"];
};

const StackHeader = ({ noSticky }: StackHeaderProps) => {
  const {
    stack,
    stackUrl,
    hasStackAdminAccess,
    handleSetConfigurationManagementViewEnabled,
    configurationManagementViewEnabled,
  } = useTypedContext(StackContext);

  const { reportSuccess } = useTypedContext(FlashContext);

  const { setStackFavorite, loading: setFavoriteLoading } = useSetStackFavorite({
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
  });

  const {
    id,
    state,
    name,
    apiHost,
    namespace,
    repository,
    repositoryURL,
    provider,
    branch,
    stateSetAt,
    administrative,
    managesStateFile,
    projectRoot,
    spaceDetails,
    vendorConfig,
    effectiveTerraformVersion,
    blueprint,
  } = stack;

  const isAnsibleVendor = isAnsibleStackVendor(stack) && configurationManagementViewEnabled;
  const isTerraformVendor = vendorConfig?.__typename === VENDOR_CONFIG_TYPENAME.TERRAFORM;
  const isTerragruntVendor = vendorConfig?.__typename === VENDOR_CONFIG_TYPENAME.TERRAGRUNT;

  const prsTabName = provider === VcsProvider.Gitlab ? "MRs" : "PRs";

  const menuItems = useMemo(
    (): NavigationItem[] => [
      {
        key: MenuItems.Runs,
        link: stackUrl,
        title: "Tracked runs",
        visible: true,
        exact: true,
      },
      {
        key: MenuItems.IgnoredRuns,
        link: `${stackUrl}/ignored-runs`,
        title: "Ignored runs",
        visible: true,
      },
      {
        key: MenuItems.Tasks,
        link: `${stackUrl}/tasks`,
        title: "Tasks",
        visible: true,
      },
      {
        key: MenuItems.PRs,
        link: `${stackUrl}/prs`,
        title: `${prsTabName}`,
        visible: true,
      },
      {
        key: MenuItems.Environment,
        link: `${stackUrl}/environment`,
        title: "Environment",
        visible: true,
      },
      {
        key: MenuItems.Hooks,
        link: `${stackUrl}/hooks`,
        title: "Hooks",
        visible: true,
      },
      {
        key: MenuItems.Contexts,
        link: `${stackUrl}/contexts`,
        title: "Contexts",
        visible: true,
      },
      {
        key: MenuItems.IacManagement,
        link: `${stackUrl}/resources`,
        title: "Resources",
        visible: !isAnsibleVendor,
      },
      {
        key: MenuItems.ConfigurationManagement,
        link: `${stackUrl}/configuration-management`,
        title: "Resources",
        visible: isAnsibleVendor,
      },
      {
        key: MenuItems.Outputs,
        link: `${stackUrl}/outputs`,
        title: "Outputs",
        visible: !isAnsibleVendor,
      },
      {
        key: MenuItems.Dependencies,
        link: `${stackUrl}/dependencies`,
        title: "Dependencies",
        visible: true,
      },
      {
        key: MenuItems.Notifications,
        link: `${stackUrl}/notifications`,
        title: "Notifications",
        visible: true,
      },
      {
        key: MenuItems.Scheduling,
        link: `${stackUrl}/scheduling`,
        title: "Scheduling",
        visible: true,
      },
      {
        key: MenuItems.Policies,
        link: `${stackUrl}/policies`,
        title: "Policies",
        visible: true,
      },
      {
        key: MenuItems.StateHistory,
        link: `${stackUrl}/state-history`,
        title: "State history",
        visible: isTerraformVendor && managesStateFile,
      },
      {
        key: MenuItems.Workspaces,
        link: `${stackUrl}/workspaces`,
        title: "Workspaces",
        visible: isTerragruntVendor,
      },
    ],
    [isAnsibleVendor, isTerraformVendor, isTerragruntVendor, managesStateFile, stackUrl, prsTabName]
  );

  const handleStarStack = (clb?: () => void) => () => {
    setStackFavorite({ id: stack.id, star: !stack.starred }, (data) => {
      if (typeof data?.starSet?.starred === "boolean") {
        reportSuccess({
          message: data.starSet.starred
            ? "Stack added to favorites"
            : "Stack removed from favorites",
        });
      }

      clb?.();
    });
  };

  const handleOpenInstructions = useCallback(() => {
    showTerraformGeneratedCodeDrawer({
      stack: stack,
    });
  }, [stack]);

  const handleOpenDetailsDrawer = () => {
    showStackDetailsDrawer({ stack, stackUrl, hasStackAdminAccess });
  };

  const canDisableStack = useCanDisableStack()(stack);
  const canEnableStack = useCanEnableStack()(stack);

  const isSpaceliftProvider = provider === VcsProvider.Spacelift;
  // A provider without a VCS host (empty apiHost) has nothing to link to, so its repository
  // and branch are shown as plain, non-linked text. Blob Storage is the only one today.
  const hasVcsHost = providerHasVcsHost(provider);

  return (
    <ViewHeader noSticky={noSticky}>
      <ViewHeaderWrapper direction="row" justify="between" fullWidth>
        <ViewHeaderWrapper direction="row" align="center">
          <ViewHeaderTitle>{name}</ViewHeaderTitle>
          {stack.vcsDetached && <VCSDetachedIndicator className={styles.vcsDetachedIndicator} />}
          <StackStateBadge state={state} />
        </ViewHeaderWrapper>

        <ViewHeaderWrapper direction="row" align="end" shrink="0">
          {isAnsibleStackVendor(stack) && (
            <Box padding="0 0 small 0" id="enable-configuration-management-view">
              <FormToggleField
                variant="switch"
                title="Enable configuration management view"
                checked={configurationManagementViewEnabled}
                onChange={handleSetConfigurationManagementViewEnabled}
              />
            </Box>
          )}
          <StackHeaderAskButton />

          <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
            Details
          </Button>

          <DropdownMenuEllipsis
            tooltip="Stack actions"
            id="stack-view--dropdown-actions-with-settings"
            dotsSize="medium"
          >
            <CopyFieldDropdownMenuItem title="Copy ID" value={id} />
            <DropdownMenuItem
              onAction={handleStarStack(close)}
              loading={setFavoriteLoading}
              isDisabled={setFavoriteLoading}
            >
              {stack.starred ? "Remove from favorites" : "Add to favorites"}
            </DropdownMenuItem>
            {(canDisableStack.result || canDisableStack.message) && (
              <DisableButton
                disabled={!!canDisableStack.message}
                tooltip={canDisableStack.message}
                stackId={id}
              />
            )}
            {(canEnableStack.result || canEnableStack.message) && (
              <EnableButton
                disabled={!!canEnableStack.message}
                tooltip={canEnableStack.message}
                stackId={id}
              />
            )}
            <DropdownMenuItem href={`${stackUrl}/settings`}>Settings</DropdownMenuItem>
            <DropdownMenuItem onAction={handleOpenInstructions}>As Terraform code</DropdownMenuItem>
          </DropdownMenuEllipsis>
        </ViewHeaderWrapper>
      </ViewHeaderWrapper>

      <ViewHeaderScrollCollapse>
        <MetaInfoList>
          {stack.isStateRollback && managesStateFile && (
            // TODO: refactor MetaInfoListItem, add the "color" prop
            <MetaInfoListItem icon={ClockRewind} className={styles.rolledBack}>
              State rolled back
            </MetaInfoListItem>
          )}
          {spaceDetails && (
            <SpacePath variant="meta" spaceId={spaceDetails.id} fallbackName={spaceDetails.name} />
          )}
          {blueprint && (
            <MetaInfoBlueprint
              blueprint={blueprint}
              isGeneratedByTemplate={!!stack.templateDeployment}
            />
          )}
          <MetaInfoVendor
            vendorConfig={vendorConfig}
            effectiveTerraformVersion={effectiveTerraformVersion}
          />
          {isSpaceliftProvider && <MetaInfoSpaceliftRepo vcsIntegration={stack.vcsIntegration} />}
          {!hasVcsHost && (
            <MetaInfoListItem icon={VCS_PROVIDERS_ICONS[provider]}>{repository}</MetaInfoListItem>
          )}
          {apiHost && (
            <MetaInfoListItem
              linkText={generateRepoUrlText({
                namespace,
                repository,
              })}
              href={generateRepoUrl({
                apiHost,
                namespace,
                repository,
                repositoryURL,
                provider,
              })}
              icon={VCS_PROVIDERS_ICONS[provider]}
              target="_blank"
            />
          )}
          {projectRoot && apiHost && (
            <MetaInfoListItem
              linkText={projectRoot}
              href={generateBranchUrl({
                apiHost,
                branch,
                namespace,
                repository,
                repositoryURL,
                provider,
                projectRoot,
              })}
              icon={Terminal}
              target="_blank"
            />
          )}
          {projectRoot && !apiHost && (
            <MetaInfoProjectRoot projectRoot={projectRoot} tooltip={projectRootTooltipContent} />
          )}
          {apiHost && (
            <MetaInfoListItem
              linkText={branch}
              href={generateBranchUrl({
                apiHost,
                namespace,
                repository,
                repositoryURL,
                provider,
                branch,
              })}
              icon={GitBranch}
              target="_blank"
            />
          )}
          {!isSpaceliftProvider && <MetaInfoStackCommit stack={stack} />}
          {isSpaceliftProvider && (
            <MetaInfoSpaceliftCommit
              vcsIntegration={stack.vcsIntegration}
              commit={stack.trackedCommit}
              commitSetBy={stack.trackedCommitSetBy}
              provider={provider}
            />
          )}
          {stateSetAt && <TimestampMetaInfo prefix="State updated on" timestamp={stateSetAt} />}
          {administrative && <MetaInfoListItem>Administrative</MetaInfoListItem>}
          {isTerraformVendor && managesStateFile && (
            <MetaInfoListItem>
              <ManagedStateInfo />
            </MetaInfoListItem>
          )}
        </MetaInfoList>
      </ViewHeaderScrollCollapse>

      <DesktopView>
        <ViewHeaderNavigationResize items={menuItems} hideOrder={hideOrder} />
      </DesktopView>
      <MobileView>
        <ViewHeaderNavigation>
          <ViewHeaderNavigationItems items={menuItems} />
        </ViewHeaderNavigation>
      </MobileView>
    </ViewHeader>
  );
};

export default StackHeader;
